/**
 * Emotion Cache for SSR Hydration
 *
 * This file creates a shared Emotion cache that ensures styles are consistent
 * between server-side rendering (SSR) and client-side hydration.
 *
 * ## Why This Exists
 *
 * Without a shared cache, Chakra UI v3 styles would:
 * 1. Render correctly on the server
 * 2. DISAPPEAR after hydration
 *
 * This causes a visible "flash of unstyled content" (FOUC).
 *
 * ## How It Works
 *
 * 1. `_document.tsx` creates this cache during SSR and extracts critical CSS
 * 2. The extracted CSS is injected as `<style>` tags in the HTML
 * 3. `_app.tsx` wraps the app with `CacheProvider` using the same cache
 * 4. The `emotion-insertion-point` meta tag ensures styles inject in the right place
 *
 * ## Related Files
 *
 * - `pages/_document.tsx` - Extracts critical CSS during SSR
 * - `pages/_app.tsx` - Provides the cache to the app
 * - `ThemeProvider.tsx` - Wraps ChakraProvider with the emotion cache
 *
 * @see https://emotion.sh/docs/ssr#nextjs
 */
import createCache from "@emotion/cache";

const isBrowser = typeof document !== "undefined";

/**
 * Creates an Emotion cache with a consistent insertion point.
 *
 * The insertion point is a `<meta name="emotion-insertion-point">` tag
 * defined in `_document.tsx`. This ensures Emotion styles are inserted
 * in a predictable location in the `<head>`.
 */
export default function createEmotionCache() {
  let insertionPoint: HTMLElement | undefined;

  if (isBrowser) {
    // Find the insertion point meta tag defined in _document.tsx
    const emotionInsertionPoint = document.querySelector<HTMLMetaElement>(
      'meta[name="emotion-insertion-point"]'
    );
    insertionPoint = emotionInsertionPoint ?? undefined;
  }

  // "chakra" key namespaces all Chakra UI styles
  return createCache({ key: "chakra", insertionPoint });
}
